import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const LANGUAGE_STORAGE_KEY = "site-language";
const DEFAULT_LANGUAGE = "en";
const TARGET_LANGUAGE = "ar";
const TRANSLATE_COOKIE = "googtrans";

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  isArabic: false,
  setLanguage: () => {},
  toggleLanguage: () => {},
});

const clearGoogleTranslateCookie = () => {
  if (typeof document === "undefined") {
    return;
  }

  const hostname = window.location.hostname;
  const expire = `${TRANSLATE_COOKIE}=;path=/;max-age=0`;
  document.cookie = expire;
  document.cookie = `${TRANSLATE_COOKIE}=;path=/;domain=.${hostname};max-age=0`;
  document.cookie = `${TRANSLATE_COOKIE}=;path=/;domain=${hostname};max-age=0`;
};

const removeGoogleTranslateArtifacts = () => {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.add("notranslate");
  document.body.classList.add("notranslate");

  document
    .querySelectorAll(
      '#google_translate_element, iframe.goog-te-banner-frame, iframe.skiptranslate, .goog-te-banner-frame, .goog-te-balloon-frame, .goog-te-menu-frame, #goog-gt-tt, div[class*="VIpgJd-"]'
    )
    .forEach((node) => node.remove());

  document.body.style.setProperty("top", "0", "important");
  document.body.style.setProperty("position", "static", "important");
};

const setDocumentDirection = (language) => {
  const isArabic = language === TARGET_LANGUAGE;
  document.documentElement.lang = isArabic ? TARGET_LANGUAGE : DEFAULT_LANGUAGE;
  document.documentElement.dir = isArabic ? "rtl" : "ltr";
  document.body.classList.toggle("rtl-layout", isArabic);
};

function applyLanguageToDocument(language) {
  if (typeof document === "undefined") {
    return;
  }

  clearGoogleTranslateCookie();
  removeGoogleTranslateArtifacts();
  setDocumentDirection(language);
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(
    () => localStorage.getItem(LANGUAGE_STORAGE_KEY) || TARGET_LANGUAGE
  );
  const languageRef = useRef(language);

  const setLanguage = useCallback((nextLanguage) => {
    languageRef.current = nextLanguage;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
    applyLanguageToDocument(nextLanguage);
  }, []);

  const toggleLanguage = useCallback(() => {
    const nextLanguage = languageRef.current === DEFAULT_LANGUAGE ? TARGET_LANGUAGE : DEFAULT_LANGUAGE;
    setLanguage(nextLanguage);
  }, [setLanguage]);

  useEffect(() => {
    languageRef.current = language;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    applyLanguageToDocument(language);
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      isArabic: language === TARGET_LANGUAGE,
      setLanguage,
      toggleLanguage,
    }),
    [language, setLanguage, toggleLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);
export { applyLanguageToDocument };
